import {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export type SheetSnap = "peek" | "half" | "full";
const SNAP_ORDER: SheetSnap[] = ["peek", "half", "full"];
// full-expand 시 스테이지 상단 여백(px). 프랙션 아닌 고정 px(가이드 §7-5).
const FULL_TOP = 56;
const PEEK_FALLBACK = 120;

interface UseBottomSheetOptions {
  // 스냅 변경 시 호출(지도 재핏 계약). coveredPx = 시트에 가려진(=지도에서 안 보이는) 하단 높이.
  onSnapChange?: (snap: SheetSnap, coveredPx: number) => void;
  enabled?: boolean; // 모바일에서만 활성
}

// 시트는 스테이지(relative 컨테이너) 안에서 absolute. 아래로 밀어 peek/half/full 노출.
function translateFor(snap: SheetSnap, stageH: number, peekPx: number): number {
  const sheetH = Math.max(0, stageH - FULL_TOP);
  if (snap === "full") return 0;
  if (snap === "half") return Math.max(0, sheetH - Math.round(stageH * 0.5));
  return Math.max(0, sheetH - peekPx); // peek
}

// 보이는 시트 높이(=지도에서 시트에 가려진 하단 높이). 지도 refit 하단 패딩에 사용.
function coveredPxFor(snap: SheetSnap, stageH: number, peekPx: number): number {
  const sheetH = Math.max(0, stageH - FULL_TOP);
  return sheetH - translateFor(snap, stageH, peekPx);
}

export function useBottomSheet(opts: UseBottomSheetOptions = {}) {
  const { onSnapChange, enabled = true } = opts;
  const [snap, setSnapState] = useState<SheetSnap>("half");
  const [stageH, setStageH] = useState(0);
  const [peekPx, setPeekPx] = useState(PEEK_FALLBACK);

  const stageRef = useRef<HTMLDivElement | null>(null);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  // 드래그 상태는 ref(리렌더 없이) — 드래그 중 transform 만 imperative, 스냅만 React state.
  const drag = useRef<{ active: boolean; startY: number; startT: number }>({
    active: false,
    startY: 0,
    startT: 0,
  });

  // 스테이지 높이 실측(ResizeObserver).
  useLayoutEffect(() => {
    if (!enabled || !stageRef.current) return;
    const el = stageRef.current;
    const measure = () => setStageH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [enabled]);

  // peek 높이 = 핸들 + 파트너/요약 헤더 실측.
  useLayoutEffect(() => {
    if (!enabled || !headerRef.current) return;
    const el = headerRef.current;
    const measure = () => setPeekPx(Math.max(64, el.offsetHeight));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [enabled]);

  const commitSnap = useCallback(
    (next: SheetSnap) => {
      setSnapState(next);
      onSnapChange?.(next, coveredPxFor(next, stageH, peekPx));
    },
    [onSnapChange, stageH, peekPx],
  );

  // 스냅/치수 변경 시 지도 재핏 통지.
  useEffect(() => {
    if (!enabled || !stageH) return;
    onSnapChange?.(snap, coveredPxFor(snap, stageH, peekPx));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap, stageH, peekPx, enabled]);

  const applyTransform = (y: number, withTransition: boolean) => {
    const el = sheetRef.current;
    if (!el) return;
    el.style.transition = withTransition
      ? "transform 0.28s cubic-bezier(0.32,0.72,0,1)"
      : "none";
    el.style.transform = `translateY(${y}px)`;
  };

  // 드래그 중 window pointermove/up. 릴리스 시 가장 가까운 디텐트로 스냅.
  useEffect(() => {
    if (!enabled) return;
    const clampY = (clientY: number) => {
      const sheetH = Math.max(0, stageH - FULL_TOP);
      const maxT = Math.max(0, sheetH - peekPx);
      return Math.min(
        maxT,
        Math.max(0, drag.current.startT + (clientY - drag.current.startY)),
      );
    };
    const onMove = (e: PointerEvent) => {
      if (!drag.current.active) return;
      applyTransform(clampY(e.clientY), false);
    };
    const onUp = (e: PointerEvent) => {
      if (!drag.current.active) return;
      drag.current.active = false;
      const y = clampY(e.clientY);
      let best: SheetSnap = "peek";
      let bd = Infinity;
      for (const s of SNAP_ORDER) {
        const d = Math.abs(translateFor(s, stageH, peekPx) - y);
        if (d < bd) {
          bd = d;
          best = s;
        }
      }
      // 인라인 transform 제거 → base(style prop)로 복귀하며 트랜지션(잔류 갭 방지).
      const el = sheetRef.current;
      if (el) {
        el.style.transition = "transform 0.28s cubic-bezier(0.32,0.72,0,1)";
        el.style.transform = "";
      }
      commitSnap(best);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [enabled, stageH, peekPx, commitSnap]);

  const onHandlePointerDown = (e: React.PointerEvent) => {
    if (!enabled) return;
    drag.current = {
      active: true,
      startY: e.clientY,
      startT: translateFor(snap, stageH, peekPx),
    };
  };

  // 핸들 탭 → 한 단계 승격(peek→half→full), full 에서 탭이면 peek 로 접기.
  const promote = () => {
    const i = SNAP_ORDER.indexOf(snap);
    commitSnap(i < SNAP_ORDER.length - 1 ? SNAP_ORDER[i + 1] : "peek");
  };

  const baseTranslate = translateFor(snap, stageH, peekPx);
  const sheetStyle: CSSProperties = enabled
    ? {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: `calc(100% - ${FULL_TOP}px)`,
        transform: `translateY(${baseTranslate}px)`,
        transition: "transform 0.28s cubic-bezier(0.32,0.72,0,1)",
        touchAction: "none",
        overscrollBehavior: "contain",
        zIndex: 10,
      }
    : {};

  return {
    snap,
    setSnap: commitSnap,
    promote,
    stageRef,
    sheetRef,
    headerRef,
    contentRef,
    sheetStyle,
    onHandlePointerDown,
    // half·full 에서 스크롤 허용(peek 잠금). 마커 탭→행 자동 스크롤이 half 에서도 동작하도록.
    contentScrollable: snap !== "peek",
    coveredPx: coveredPxFor(snap, stageH, peekPx),
  };
}
